// The package exports `Intercom` as both the default and a named export;
// aliasing the default avoids the import/no-named-as-default lint warning.
import initIntercom, { shutdown, update } from '@intercom/messenger-js-sdk'
import { inE2E } from './environment'

// Intercom Messenger, home of the Fin support agent. Replaces the HubSpot
// Conversations widget.
//
// Unlike HubSpot, Intercom has no portal-side URL targeting — the Messenger
// appears wherever the code runs. The route exclusions that used to live in
// HubSpot's chatflow settings therefore live here instead, in
// isSuppressedPath.
//
// The domains this touches are allowlisted in the CSP in firebase.json. Which
// domains may host the Messenger at all is configured separately, under
// Messenger settings > General > "Keep your Messenger secure".

/**
 * Public workspace identifier. Safe to ship in the client bundle — every
 * Intercom customer exposes theirs in page source. Hardcoded rather than read
 * from a `VITE_` env var, matching the Sentry DSN in config/sentry.ts.
 *
 * The Messenger API secret used for identity verification is NOT here and must
 * never be: anything prefixed `VITE_` is inlined into the client bundle at
 * build time. Identity verification is a server-side follow-up.
 */
const APP_ID = 'yvzr25hq'

/**
 * US-hosted workspace. EU workspaces use `https://api-iam.eu.intercom.io` and
 * AU workspaces `https://api-iam.au.intercom.io`. This must match where the
 * workspace actually lives or the Messenger silently fails to connect.
 */
const API_BASE = 'https://api-iam.intercom.io'

let started = false

/**
 * Routes that must never show the launcher. The launcher is `position: fixed`
 * bottom-right and would otherwise sit on top of the meeting UI.
 *
 * `/admin/devmode/` renders the same MeetingPage behind a role guard, so it is
 * excluded too even though its path contains no `/meeting` segment.
 *
 * Exported for tests — this is the logic that replaces HubSpot's portal-side
 * URL targeting, so it is the part most worth pinning down.
 */
export function isSuppressedPath(path: string | undefined) {
  if (!path) return false
  return path.includes('/meeting') || path.startsWith('/admin/devmode')
}

/**
 * Boot the Messenger anonymously. Idempotent, and a no-op in E2E runs (we don't
 * want a third-party iframe and its network calls making tests flaky).
 *
 * Anonymous boot is intentional: logged-out visitors stuck on login or a failed
 * LTI launch are a real support case.
 */
export function startIntercom() {
  if (inE2E()) return
  if (started) return
  started = true

  initIntercom({
    app_id: APP_ID,
    api_base: API_BASE,
  })
}

/**
 * Attach the signed-in user to the current Messenger session. Called from
 * useUserEffects once the profile has loaded.
 *
 * Note this is unverified until we add JWT identity verification server-side —
 * Intercom takes these attributes on trust, exactly as the HubSpot widget did.
 */
export function identifyIntercomUser(params: {
  userId: string
  emailAddress?: string
  name?: string
}) {
  if (inE2E() || !started) return

  update({
    user_id: params.userId,
    email: params.emailAddress,
    name: params.name,
  })
}

/**
 * End the Intercom session on logout and return the Messenger to its anonymous
 * state. `shutdown` clears the session cookie, which matters on the shared
 * classroom machines this product runs on — without it the next user inherits
 * the previous user's conversation history.
 */
export function resetIntercom() {
  if (inE2E() || !started) return

  stopIntercom()
  startIntercom()
}

/**
 * Tear the Messenger down without booting it again. Used when the
 * chatbotProvider flag moves away from Fin at runtime - without this, flipping
 * the flag would leave the Intercom launcher on screen next to whatever
 * mounted in its place.
 */
export function stopIntercom() {
  if (inE2E() || !started) return

  shutdown()
  started = false
}

/**
 * Show or hide the launcher bubble.
 *
 * Uses `hide_default_launcher` rather than the SDK's `hide()`: `hide()` only
 * closes an open Messenger window and leaves the bubble on screen. Hiding this
 * way also preserves the session, so a user mid-conversation doesn't lose it
 * when they start a quiz.
 */
export function setIntercomLauncherHidden(hidden: boolean) {
  if (inE2E() || !started) return

  update({ hide_default_launcher: hidden })
}
